import { z } from 'zod';
import { AddressSchema } from './address';
import { ISODateSchema, UUIDSchema } from './common';
import { EnergySummarySchema } from './energy';
import { FileCategorySchema } from './file';
import { AreaValueSchema, PercentageSchema } from './measurements';
import { MonetaryValueSchema, SquareMetreMoneyPerAreaValueSchema } from './money';
import { OrderStatusSchema } from './order';
import { AggregatedValueOverviewTenantSummarySchema, ValueOverviewTenantSummarySchema } from './tenant-summary';
import { ValuationStatusSchema, ValuationTypeSchema } from './valuation-primitives';
import { AreaValueWithTrendSchema, MonetaryValueWithTrendSchema, NumberValueWithTrendSchema, PercentageWithTrendSchema, } from './trend-values';
const RentOverviewShape = {
    summary: ValueOverviewTenantSummarySchema,
};
export const RentOverviewSchema = z.object(RentOverviewShape);
export const ValueOverviewAddressSchema = AddressSchema.pick({
    country: true,
    city: true,
    street: true,
    house_number: true,
    state: true,
    zip: true,
    coordinates: true,
});
export const ValueOverviewStructureUsageTypeSchema = z.enum(['residential', 'office', 'retail', 'mixed', 'garage', 'other']);
const ValueOverviewAssetStructureSchema = z.object({
    title: z.string(),
    construction_year: z.number(),
    usage_type: ValueOverviewStructureUsageTypeSchema,
    floor_description: z.string().optional(),
    zoning_of_usage: z.string().optional(),
    building_access: z.string().optional(),
});
export const AssetEconomicUnitOverviewSchema = z.object({
    title: z.string(),
    structures: ValueOverviewAssetStructureSchema.array(),
});
export const ValueOverviewRentSituationSchema = z.enum([
    'excellent',
    'very_good',
    'good',
    'above_average',
    'slightly_above_average',
    'average',
    'slightly_below_average',
    'below_average',
    'mediocre',
    'bad',
    'catastrophic',
]);
export const AssetRatingSchema = z.object({
    total: z.number().optional(),
    structures: z.number().optional(),
    location: z.number().optional(),
    micro_location: z.number().optional(),
    macro_location: z.number().optional(),
    real_estate_market: z.number().optional(),
    real_estate_economics: z.number().optional(),
    real_estate_structure: z.number().optional(),
    rent_situation: ValueOverviewRentSituationSchema.optional(),
});
export const ReasonSchema = z.object({
    type: z.enum(['location', 'asset', 'rent', 'other']),
    label: z.string(),
});
export const MarketComparisonItemSchema = z.object({
    market_comparison_rating: z
        .enum(['very_above_average', 'slightly_above_average', 'average', 'slightly_below_average', 'very_below_average', 'unable_to_rate'])
        .optional(),
    reasons: ReasonSchema.array(),
});
export const MarketComparisonSchema = z.object({
    market_value: MarketComparisonItemSchema,
    yield_and_multiplier: MarketComparisonItemSchema,
    rent: MarketComparisonItemSchema,
});
export const ObservationTimeSchema = z.union([
    z.object({
        time_type: z.literal('date'),
        date: ISODateSchema,
    }),
    z.object({
        time_type: z.literal('range'),
        custom_range: z.string(),
    }),
]);
export const SourceDateSchema = z.union([
    z.object({
        date_type: z.literal('year'),
        year: z.number(),
    }),
    z.object({
        date_type: z.literal('month_year'),
        year: z.number(),
        month: z.number(),
    }),
    z.object({
        date_type: z.literal('day_month_year'),
        date: ISODateSchema,
    }),
]);
const ComparableFileSchema = z.object({
    uuid: UUIDSchema,
    name: z.string(),
    created_on: ISODateSchema,
    category: FileCategorySchema,
    url: z.string(),
    tags: z.string().array().optional(),
    content_type: z.string(),
});
export const DataSourceSchema = z.union([
    z.object({
        title: z.string(),
        publisher: z.string(),
        region: z.string(),
        notes: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        source_type: z.literal('web'),
        url: z.string(),
        retrieved_on: ISODateSchema,
    }),
    z.object({
        title: z.string(),
        publisher: z.string(),
        region: z.string(),
        notes: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        source_type: z.literal('file'),
        file: ComparableFileSchema.optional(),
        source_date: SourceDateSchema.optional(),
        hide_source_file: z.boolean().optional(),
    }),
    z.object({
        title: z.string(),
        publisher: z.string(),
        region: z.string(),
        notes: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        source_type: z.literal('none'),
    }),
]);
export const MonetaryDataValueSchema = z.object({
    value_type: z.literal('monetary'),
    label: z.string(),
    value: MonetaryValueSchema,
});
export const MoneyPerAreaDataValueSchema = z.object({
    value_type: z.literal('money_per_area'),
    label: z.string(),
    value: SquareMetreMoneyPerAreaValueSchema,
});
export const PercentageDataValueSchema = z.object({
    value_type: z.literal('percentage'),
    label: z.string(),
    value: PercentageSchema,
});
export const NumberDataValueSchema = z.object({
    value_type: z.literal('float'),
    label: z.string(),
    value: z.number(),
});
export const ComparablesStatisticsSchema = z.object({
    num_relevant_records: z.number(),
    num_total_records: z.number(),
});
export const ComparablesDataEntrySchema = z.union([
    z.object({
        usage_type: ValueOverviewStructureUsageTypeSchema,
        short_description: z.string().optional(),
        foot_note: z.string().optional(),
        full_description: z.string().optional(),
        statistics: ComparablesStatisticsSchema.optional(),
        region: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        data_type: z.literal('market_value'),
        values: MonetaryDataValueSchema.array(),
    }),
    z.object({
        usage_type: ValueOverviewStructureUsageTypeSchema,
        short_description: z.string().optional(),
        foot_note: z.string().optional(),
        full_description: z.string().optional(),
        statistics: ComparablesStatisticsSchema.optional(),
        region: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        data_type: z.literal('market_area_value'),
        values: MoneyPerAreaDataValueSchema.array(),
    }),
    z.object({
        usage_type: ValueOverviewStructureUsageTypeSchema,
        short_description: z.string().optional(),
        foot_note: z.string().optional(),
        full_description: z.string().optional(),
        statistics: ComparablesStatisticsSchema.optional(),
        region: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        data_type: z.literal('yearly_rent'),
        values: MoneyPerAreaDataValueSchema.array(),
    }),
    z.object({
        usage_type: ValueOverviewStructureUsageTypeSchema,
        short_description: z.string().optional(),
        foot_note: z.string().optional(),
        full_description: z.string().optional(),
        statistics: ComparablesStatisticsSchema.optional(),
        region: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        data_type: z.literal('monthly_rent'),
        values: MoneyPerAreaDataValueSchema.array(),
    }),
    z.object({
        usage_type: ValueOverviewStructureUsageTypeSchema,
        short_description: z.string().optional(),
        foot_note: z.string().optional(),
        full_description: z.string().optional(),
        statistics: ComparablesStatisticsSchema.optional(),
        region: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        data_type: z.literal('yield'),
        values: PercentageDataValueSchema.array(),
    }),
    z.object({
        usage_type: ValueOverviewStructureUsageTypeSchema,
        short_description: z.string().optional(),
        foot_note: z.string().optional(),
        full_description: z.string().optional(),
        statistics: ComparablesStatisticsSchema.optional(),
        region: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        data_type: z.literal('multiplier'),
        values: NumberDataValueSchema.array(),
    }),
    z.object({
        usage_type: ValueOverviewStructureUsageTypeSchema,
        short_description: z.string().optional(),
        foot_note: z.string().optional(),
        full_description: z.string().optional(),
        statistics: ComparablesStatisticsSchema.optional(),
        region: z.string().optional(),
        observation_time: ObservationTimeSchema.optional(),
        data_type: z.literal('discount_rate'),
        values: PercentageDataValueSchema.array(),
    }),
]);
export const ComparableDataSchema = z.object({
    entries: ComparablesDataEntrySchema.array().optional(),
    source: DataSourceSchema,
});
export const ComparablesSchema = z.object({
    market_data: ComparableDataSchema.array(),
    market_comparison: MarketComparisonSchema,
});
export const SensitivityMatrixValuesSchema = z.object({
    asset_value: MonetaryValueSchema,
    asset_value_per_sqm: SquareMetreMoneyPerAreaValueSchema,
    delta_to_base: z.number(),
    yield_rate: z.number(),
    multiplier: z.number(),
});
const SensitivityMatrixDiscountRateSchema = z.object({
    base_discount_rate: SensitivityMatrixValuesSchema,
    increased_discount_rate: SensitivityMatrixValuesSchema,
    decreased_discount_rate: SensitivityMatrixValuesSchema,
});
const SensitivityMatrixResultsSchema = z.object({
    base_rent: SensitivityMatrixDiscountRateSchema,
    increased_rent: SensitivityMatrixDiscountRateSchema,
    decreased_rent: SensitivityMatrixDiscountRateSchema,
});
export const ValueOverviewSensitivityMatrixSchema = z.object({
    rent_change: z.number(),
    discount_rate_change: z.number(),
    results: SensitivityMatrixResultsSchema,
});
export const ValueByValuationMethodSchema = z
    .object({
    cost: MonetaryValueSchema.optional(),
    income: MonetaryValueSchema.optional(),
    comparison: MonetaryValueSchema.optional(),
    land_value: MonetaryValueSchema.optional(),
    custom: MonetaryValueSchema.optional(),
    leasehold: MonetaryValueSchema.optional(),
    discounted_cashflow: MonetaryValueSchema.optional(),
    investment_method: MonetaryValueSchema.optional(),
})
    .partial();
export const ValuationOverviewSchema = z.object({
    latest_valuation_uuid: UUIDSchema,
    latest_valuation_date: ISODateSchema.optional(),
    report_url: z.string().optional(),
    order_date: ISODateSchema.optional(),
    order_status: OrderStatusSchema,
    due_date: ISODateSchema.optional(),
    inspection_date: ISODateSchema.optional(),
    comparables: ComparablesSchema,
    market_value_by_valuation_method: ValueByValuationMethodSchema,
    mortgage_lending_value_by_valuation_method: ValueByValuationMethodSchema,
    market_value: MonetaryValueWithTrendSchema,
    mortgage_lending_value: MonetaryValueSchema.optional(),
    land_area: AreaValueSchema.optional(),
    gross_floor_area: AreaValueSchema.optional(),
    living_area: AreaValueSchema.optional(),
    usage_area: AreaValueSchema.optional(),
    rentable_area: AreaValueSchema.optional(),
    wault: z.union([z.number(), z.literal('unlimited')]).optional(),
    net_yield_multiplier: z.number().optional(),
    gross_yield_multiplier: z.number().optional(),
    gross_yield: PercentageSchema.optional(),
    gross_initial_yield: PercentageSchema.optional(),
    value_per_rentable_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    value_per_gross_floor_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    value_per_land_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    value_per_living_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    value_per_usage_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    rating_summary: AssetRatingSchema.optional(),
    rent_situation: ValueOverviewRentSituationSchema.optional(),
    discount_rate: PercentageSchema.optional(),
    transaction_costs: MonetaryValueSchema.optional(),
    market_value_including_transaction_costs: MonetaryValueSchema,
    sensitivity_matrix: ValueOverviewSensitivityMatrixSchema.optional(),
    is_draft: z.boolean(),
});
export const ValuationLogItemDraftOverviewSchema = z.object({
    uuid: UUIDSchema,
    title: z.string().optional(),
    market_value: MonetaryValueSchema.optional(),
    mortgage_lending_value: MonetaryValueSchema.optional(),
    report_url: z.string().optional(),
});
export const ValuationLogItemSchema = z.object({
    uuid: UUIDSchema,
    order_uuid: UUIDSchema,
    order_status: OrderStatusSchema,
    market_value: MonetaryValueSchema,
    mortgage_lending_value: MonetaryValueSchema.optional(),
    status: ValuationStatusSchema,
    valuation_type: ValuationTypeSchema,
    drafts: ValuationLogItemDraftOverviewSchema.array(),
    report_url: z.string().optional(),
    valuation_title: z.string().optional(),
    valuation_date: ISODateSchema.optional(),
    inspection_date: ISODateSchema.optional(),
    due_date: ISODateSchema.optional(),
});
const RentDataPointSchema = z.object({
    income: MonetaryValueSchema,
    costs: z
        .object({
        administrative: MonetaryValueSchema.optional(),
        maintenance: MonetaryValueSchema.optional(),
        loss_of_rent_risk: MonetaryValueSchema.optional(),
        operating: MonetaryValueSchema.optional(),
        management: MonetaryValueSchema,
    })
        .optional(),
});
export const ValuationDataPointSchema = z.object({
    valuation_date: ISODateSchema,
    valuation_uuid: UUIDSchema.optional(),
    market_value: MonetaryValueSchema.optional(),
    land_value: MonetaryValueSchema.optional(),
    rent: RentDataPointSchema.optional(),
});
export const DiscountedCashflowKpiSchema = z.object({
    discount_rate: z.number(),
    capitalization_rate: z.number(),
    net_initial_yield_on_reasonable_rent: z.number(),
    gross_initial_yield_on_reasonable_rent: z.number(),
    gross_multiplier: z.number(),
});
export const MainCashflowItemSchema = z.object({
    title: z.string(),
    total: MonetaryValueSchema,
    sub_cashflows: z.record(z.string(), MonetaryValueSchema),
});
export const RevenueCashflowSchema = z.object({
    total: MonetaryValueSchema,
    values: z.record(z.string(), MainCashflowItemSchema),
});
export const MainCostCashflowItemSchema = MainCashflowItemSchema.extend({
    category: z.enum(['capex', 'opex']),
});
export const CostCashflowSchema = z.object({
    total: MonetaryValueSchema,
    values: z.record(z.string(), MainCostCashflowItemSchema),
});
export const DcfPeriodSchema = z.object({
    start_date: ISODateSchema,
    market_rent_cashflow: RevenueCashflowSchema.optional(),
    revenue_cashflow: RevenueCashflowSchema,
    cost_cashflows: CostCashflowSchema,
    net_cashflow: MonetaryValueSchema,
    discount_factor: z.number(),
    present_value: MonetaryValueSchema,
});
export const ExitPeriodSchema = DcfPeriodSchema.extend({
    terminal_value: MonetaryValueSchema,
    end_date: ISODateSchema.optional(),
});
export const CreatePeriodsGroupSchema = z.object({
    source: DcfPeriodSchema.array(),
});
export const ExistingPeriodsGroupSchema = CreatePeriodsGroupSchema.extend({
    yearly: DcfPeriodSchema.array(),
});
export const CreateExitPeriodGroupSchema = z.object({
    source: ExitPeriodSchema,
});
export const ExistingExitPeriodGroupSchema = CreateExitPeriodGroupSchema.extend({
    yearly: ExitPeriodSchema,
});
export const CreateDiscountedCashFlowResultSchema = z.object({
    periods: CreatePeriodsGroupSchema,
    exit_period: CreateExitPeriodGroupSchema,
    present_value: MonetaryValueSchema,
    present_value_with_transaction_cost: MonetaryValueSchema.optional(),
    asset_value_rounded: MonetaryValueSchema,
    kpis: DiscountedCashflowKpiSchema,
});
export const ExistingDiscountedCashFlowResultSchema = z.object({
    periods: ExistingPeriodsGroupSchema,
    exit_period: ExistingExitPeriodGroupSchema,
    present_value: MonetaryValueSchema,
    present_value_with_transaction_cost: MonetaryValueSchema.optional(),
    asset_value_rounded: MonetaryValueSchema,
    kpis: DiscountedCashflowKpiSchema,
});
export const DiscountedCashFlowResultSchema = z.union([ExistingDiscountedCashFlowResultSchema, CreateDiscountedCashFlowResultSchema]);
export const MultiAssetValueOverviewSummarySchema = z.object({
    market_value: MonetaryValueWithTrendSchema,
    rental_income: MonetaryValueWithTrendSchema,
    actual_rental_income: MonetaryValueWithTrendSchema,
    number_of_units: NumberValueWithTrendSchema,
    number_of_assets: NumberValueWithTrendSchema,
    rentable_area: AreaValueWithTrendSchema.optional(),
    vacancy: PercentageWithTrendSchema.optional(),
    wault: z.union([z.number(), z.literal('unlimited')]).optional(),
    gross_yield: PercentageWithTrendSchema.optional(),
});
export const MultiAssetValuationDataPointSchema = z.object({
    year: z.number(),
    rental_income: MonetaryValueSchema,
    actual_rental_income: MonetaryValueSchema,
    market_value: MonetaryValueSchema,
    number_of_assets: z.number(),
});
export const NormalizedMultiAssetValuationDataPointSchema = z.object({
    year: z.number(),
    rental_income: z.number(),
    actual_rental_income: z.number(),
    market_value: z.number(),
    number_of_assets: z.number(),
});
export const NormalizedMultiAssetValuationHistorySchema = z.object({
    last_3: NormalizedMultiAssetValuationDataPointSchema.array(),
    last_5: NormalizedMultiAssetValuationDataPointSchema.array(),
    last_10: NormalizedMultiAssetValuationDataPointSchema.array(),
    last_15: NormalizedMultiAssetValuationDataPointSchema.array(),
    max: NormalizedMultiAssetValuationDataPointSchema.array(),
});
const SingleAssetValueOverviewShape = {
    uuid: UUIDSchema,
    address: ValueOverviewAddressSchema,
    customer_asset_reference: z.string().optional(),
    economic_units: AssetEconomicUnitOverviewSchema.array(),
    rent: RentOverviewSchema.optional(),
    valuation: ValuationOverviewSchema,
    valuation_log: ValuationLogItemSchema.array(),
    cost_center_uuid: UUIDSchema.optional(),
    history: ValuationDataPointSchema.array(),
    title_image: z.string().optional(),
    title_thumbnail_image: z.string().optional(),
    energy: EnergySummarySchema.optional(),
    dcf: ExistingDiscountedCashFlowResultSchema.optional(),
};
export const SingleAssetValueOverviewSchema = z.object(SingleAssetValueOverviewShape);
const MultiAssetValueOverviewShape = {
    assets: SingleAssetValueOverviewSchema.array(),
    summary: MultiAssetValueOverviewSummarySchema,
    history: MultiAssetValuationDataPointSchema.array(),
    normalized_history: NormalizedMultiAssetValuationHistorySchema.optional(),
    tenant_summary: AggregatedValueOverviewTenantSummarySchema,
};
export const MultiAssetValueOverviewSchema = z.object(MultiAssetValueOverviewShape);
const MultiPortfolioValueOverviewShape = {
    portfolios: z.lazy(() => SinglePortfolioValueOverviewSchema.array()),
    summary: MultiAssetValueOverviewSummarySchema,
    history: MultiAssetValuationDataPointSchema.array(),
    normalized_history: NormalizedMultiAssetValuationHistorySchema.optional(),
    tenant_summary: AggregatedValueOverviewTenantSummarySchema,
};
export const MultiPortfolioValueOverviewSchema = z.object(MultiPortfolioValueOverviewShape);
export const ClosingDateSchema = z.object({
    day: z.number(),
    month: z.number(),
});
const SinglePortfolioValueOverviewExtensionShape = {
    type: z.enum(['portfolio', 'synthetic']),
    uuid: UUIDSchema,
    title: z.string(),
    closing_date: ClosingDateSchema.optional(),
};
export const SinglePortfolioValueOverviewSchema = MultiAssetValueOverviewSchema.extend(SinglePortfolioValueOverviewExtensionShape);
